import {Component, Input, OnInit} from '@angular/core';
import {BluechatService} from '../../bluechat.service';
import {CarrierService} from '../../../user-dashboard/shared/carrier.service';
import {FormBuilder} from '@angular/forms';
import {NotePriority} from '../../models/NotePriority';
import {BluechatNoteAttachment, BluechatNoteCreation} from '../../models/BluechatNoteCreation';
import {UserService} from '../../../user-dashboard/shared/user.service';
import {UserInfo} from '../../../user-dashboard/shared/user-info.model';
import {ToastrService} from 'ngx-toastr';
import {FileSizePipe} from '../../../shared/pipes/file-size.pipe';
import {BluechatProcess} from '../../models/BluechatProcess';
import {BluechatTemplate} from '../../models/BluechatTemplate';
import {ModalService} from '../../../shared/services/modal.service';
import {BluechatBulkNoteCreation} from '../../models/BluechatBulkNoteCreation';
import {AuthenticationService} from '../../../shared/services/authentication.service';
import {UserBluechatConfiguration} from '../../search/bluechat-search.component';
import {TranslateService} from '@ngx-translate/core';
import {BluechatNoteModalDelivery} from '../../models/BluechatNoteModalDelivery';
import {LoaderService} from '../../../shared/services/loader.service';
import {finalize} from 'rxjs/operators';
import {DeltaOperation, DeltaStatic, Quill} from 'quill';

@Component({
  selector: 'bluechat-note-creation',
  templateUrl: './bluechat-note-creation.component.html',
  styleUrls: ['./bluechat-note-creation.component.scss']
})
export class BluechatNoteCreationComponent implements OnInit {

  @Input() deliveries: BluechatNoteModalDelivery[];

  @Input() userBluechatConfig: UserBluechatConfiguration = {
    assignedCompanies: [],
    assignedProcesses: [],
  };

  subject: string;
  noteType: string;
  process: BluechatProcess;
  message: string;
  priority: NotePriority = NotePriority.NOT_DEFINE;
  attachments: BluechatNoteAttachment[];

  _deliveries: BluechatNoteModalDelivery[];
  processesByCarrier: { [bluechatCarrierCode: string]: BluechatProcess[] };
  processes: BluechatProcess[];
  notePriority = Object.keys(NotePriority);
  currentUser: UserInfo;
  selectedProcessNoteTypes: string[];
  anySelectedCarrierNotAssignToCurrentUser = false;
  canSubmitAllBluechatNotes: boolean;
  quillEditor: Quill;

  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      []
    ]
  };
  acceptedFileTypes = BluechatService.getPodAcceptedTypes();
  fileAttachments: File[] = [];
  maxFileSizePerAttachment = 5000000;
  maxAttachmentSizePerDelivery = 10000000;
  notCommonProcesses: ProcessWithBlockingCarrier[];
  templates: BluechatTemplate[] = null;
  formProcessingBlocked = false;
  private maxTotalAttachments = 100000000;
  template: BluechatTemplate;

  constructor(
    private authService: AuthenticationService,
    private bluechatService: BluechatService,
    private carrierService: CarrierService,
    private fb: FormBuilder,
    private userService: UserService,
    private toastrService: ToastrService,
    private fileSizePipe: FileSizePipe,
    private modalService: ModalService,
    private notificationService: ToastrService,
    private translate: TranslateService,
    private loader: LoaderService,
  ) {
  }

  ngOnInit() {
    this.canSubmitAllBluechatNotes = this.authService.canSubmitAllBluechatNotes();
    this._deliveries = [...this.deliveries];

    const distinctCarrierBluechatCodes = new Set<string>(this._deliveries.map(el => el.carrierBluechatCode)).values()
    this.bluechatService.getProcesses(Array.from(distinctCarrierBluechatCodes))
      .subscribe(processes => {
        this.processesByCarrier = processes;
        this.processes = this.getDistinctProcesses(this.processesByCarrier);
        this.notCommonProcesses = this.getNonCommonProcess(this.processesByCarrier);
        this.templates = null;
      });

    this.refreshAnySelectedCarrierNotAssignToCurrentUser();

    this.userService.getSelf().subscribe(user => this.currentUser = user);

  }

  refreshAnySelectedCarrierNotAssignToCurrentUser() {
    this.anySelectedCarrierNotAssignToCurrentUser = this._deliveries.some(l => !this.userBluechatConfig.assignedCompanies.some(c => c.code === l.carrierBluechatCode))
      && this.canSubmitAllBluechatNotes;
  }

  onProcessChange(newProcess: BluechatProcess) {
    if (!newProcess){
      this.selectedProcessNoteTypes = null;
      this.process = null;
      this.template = null;
      this.templates = null;
      this.noteType = null;
      return;
    }

    this.selectedProcessNoteTypes = newProcess ? newProcess.categories : [];
    if (!this.selectedProcessNoteTypes.includes(this.noteType)) {
      this.noteType = null;
      this.templates = null;
      this.template = null;
    }
  }

  getNonCommonProcess(processesByCarrier: { [bluechatCarrierCode: string]: BluechatProcess[] }): ProcessWithBlockingCarrier[] {
    if (Object.keys(processesByCarrier).length === 0) {
      return [];
    }

    return this.getDistinctProcesses(processesByCarrier)
      .filter(p => !Object.values(processesByCarrier).every(cProcesses => cProcesses.some(cProcess => cProcess.id === p.id)) || !this.userBluechatConfig.assignedProcesses.some(up => up.id === p.id))
      .map(p => ({
        process: p,
        blockingCarriers: this.getBlockingCarriers(p.id, processesByCarrier),
      }));
  }

  intersect(base: BluechatProcess[], next: BluechatProcess[]): BluechatProcess[] {
    const ids = next.map(el => el.id);
    return base.filter(el => ids.includes(el.id));
  }

  distinctProcessses(src: BluechatProcess[]): BluechatProcess[] {
    return src.filter((el, i) => src.indexOf(src.find(it => it.id === el.id)) === i);
  }

  addToAttachments(file: File) {
    if (!file) { return; }
    const nbdeliveries = this._deliveries.length;
    if (
      this.checkAttachmentsSizeAreUnder(this.maxAttachmentSizePerDelivery, [... this.fileAttachments, file])
      && this.checkAttachmentsTimeDeliveriesIsUnder(this.maxTotalAttachments, [... this.fileAttachments, file], nbdeliveries)
    ) {
      this.fileAttachments.push(file);
      this.attachments = this.bluechatService.fileToAttachment(this.fileAttachments);
    }
  }

  private checkAttachmentsSizeAreUnder(maxSize: number, files: File[]): boolean {
    if (files.reduce( (acc, el) => acc + el.size, 0) > maxSize ) {
      this.toastrService.error(this.translate.instant(
        'bluechat.note-creation.errors.attachment-max-size-per-delivery',
        { size: this.fileSizePipe.transform(maxSize) }
      ));
      return false;
    } else {
      return true;
    }
  }

  private checkAttachmentsTimeDeliveriesIsUnder(maxTotalAttachments: number, files: File[], nbDeliveries: number) {
    if (files.reduce( (acc, el) => acc + el.size, 0) * nbDeliveries > maxTotalAttachments ) {
      this.toastrService.error(this.translate.instant(
        'bluechat.note-creation.errors.attachment-total-max-size',
        { size: this.fileSizePipe.transform(maxTotalAttachments) }
      ));
      return false;
    } else {
      return true;
    }
  }

  private getDistinctProcesses(processesByCarrier: { [bluechatCarrierCode: string]: BluechatProcess[] }) {
    if (Object.keys(processesByCarrier).length === 0) {
      return [];
    }

    const allProcesses = Object.values(processesByCarrier).reduce((p1, p2) => p1.concat(p2), []);

    return this.distinctProcessses(allProcesses);
  }

  private getBlockingCarriers(processId: string, processesByCarrier: { [bluechatCarrierCode: string]: BluechatProcess[] }): string[] {
    return Object.keys(processesByCarrier)
      .filter(bluechatCarrierCode => !processesByCarrier[bluechatCarrierCode].some(p => p.id === processId));
  }

  isProcessDisabled(process: BluechatProcess): boolean {
    return this.notCommonProcesses.map(el => el.process.id).includes(process.id);
  }

  onTemplateChange(templateSelected: BluechatTemplate) {
    if (!templateSelected) { return; }
    this.message = templateSelected.message;
  }

  onNoteTypeChange(newNoteType: string) {
    if (!newNoteType) {
      this.template = null;
      this.templates = null;
      return;
    }
    this.refreshTemplates(this.process, newNoteType);
  }

  private refreshTemplates(process: BluechatProcess, noteType: string) {
    this.templates = null;
    this.bluechatService.getTemplates(process.title, noteType)
      .subscribe(templates => this.templates = templates);
  }

  onCarrierDelete(bluechatCarrierCode: string) {
    delete this.processesByCarrier[bluechatCarrierCode];
    this._deliveries = this._deliveries.filter(delivery => delivery.carrierBluechatCode !== bluechatCarrierCode);

    if (this._deliveries.length === 0) {
      this.modalService.triggerResolution();
    }

    this.processes = this.getDistinctProcesses(this.processesByCarrier);
    this.notCommonProcesses = this.getNonCommonProcess(this.processesByCarrier);

    if (!this.processes.some(p => p.id === this.process?.id)) {
      this.process = null;
      this.noteType = null;
    }

    this.refreshAnySelectedCarrierNotAssignToCurrentUser();
  }

  private createNoteCreations(deliveries: BluechatNoteModalDelivery[]): BluechatNoteCreation[] {
    const res: BluechatNoteCreation[] = [];

    deliveries.forEach(delivery => {
      const noteCreation = new BluechatNoteCreation();

      noteCreation.domain = this.process.title;
      noteCreation.category = this.noteType;
      noteCreation.topic = this.subject ? `${delivery.deliveryNumber} - ${this.noteType} - ${this.subject}`
        : `${delivery.deliveryNumber} - ${this.noteType}`;
      noteCreation.message = this.message;
      noteCreation.company_code = delivery.carrierBluechatCode;
      noteCreation.delivery_number = delivery.deliveryNumber;
      noteCreation.priority = this.priority;

      res.push(noteCreation)
    });

    return res;
  }

  private resetNotesField() {
    this.process = null;
    this.noteType = null;
    this.subject = null;
    this.message = null;
    this.attachments = null;
  }

  private createBulkCreation(deliveries: BluechatNoteModalDelivery[]) {
    const res = new BluechatBulkNoteCreation();
    const noteCreations = this.createNoteCreations(deliveries);

    res.attachments = this.attachments;
    res.requests = noteCreations;

    return res;
  }

  cancel() {
    this.modalService.triggerClose();
  }

  submit() {
    const bulk = this.createBulkCreation(this._deliveries);
    this.loader.startLoad();
    this.formProcessingBlocked = true;
    this.bluechatService.createBulk(bulk)
      .pipe(finalize(() => {
        this.loader.stopLoad();
        this.formProcessingBlocked = false;
      }))
      .subscribe(_ => {
        this.notificationService.success(this.translate.instant('bluechat.note-creation.successes.request-sent'));
        this.resetNotesField();
        this.modalService.triggerResolution();
    });
  }

  public removeAttachment(index: number) {
    this.fileAttachments = [...this.fileAttachments.slice(0, index), ...this.fileAttachments.slice(index + 1)];
    this.attachments = [...this.attachments.slice(0, index), ...this.attachments.slice(index + 1)];
  }

  onEditorCreated(quill): void {
    this.quillEditor = quill;
    this.quillEditor.clipboard.addMatcher(Node.ELEMENT_NODE, ((node: any, delta: DeltaStatic) => {
      const filterOutImages = this.filterOutImages(delta.ops);
      delta.ops = filterOutImages;
      return delta;
    }));
  }

  /**
   * Remove any image present in clipboard, so it's not past on the text part of the note
   * since there a button bellow to add file.
   * @param ops clipboard data
   */
  private filterOutImages(ops: DeltaOperation[]): DeltaOperation[] {
    let newOps = [];
    for (let i = 0; i < ops.length; i++) {
      if (!this.isAnImage(ops[i])) {
        newOps.push(ops[i]);
      }
    }
    return newOps;
  }

  private isAnImage(ops: DeltaOperation): boolean {
    return ops.insert && typeof ops.insert === 'object' && ops.insert.image;
  }

}

interface ProcessWithBlockingCarrier {
  process: BluechatProcess,
  blockingCarriers: string[]
}
